/**
 * AccessibilityHandler — skip-links, low-vision toggle, font-size controls.
 * Migrated from dpss-theme.js lines 267-524.
 *
 * CSS contract: body.lowvision
 * localStorage keys: "lowvision" ("true" | "false"), "fontSize" (number 80-150)
 */

import { qsa, delegate } from "./utils";

export class AccessibilityHandler {
  private readonly LABEL_ON = "Стандартна версія";
  private readonly LABEL_OFF = "Людям із порушенням зору";
  private readonly FONT_MIN = 80;
  private readonly FONT_MAX = 150;
  private readonly FONT_STEP = 10;
  private readonly FONT_DEFAULT = 100;

  constructor() {
    this.init();
  }

  private init(): void {
    this.initSkipLinks();
    this.initVisionToggle();
    this.initFontSize();
  }

  // ── Skip-link ────────────────────────────────────────────────
  private initSkipLinks(): void {
    const skipLink = document.querySelector<HTMLAnchorElement>(".skip-link");
    if (!skipLink) return;

    skipLink.addEventListener("click", () => {
      const targetId = skipLink.getAttribute("href");
      if (!targetId) return;
      const target = document.querySelector<HTMLElement>(targetId);
      if (target) {
        if (target.tabIndex < 0) target.tabIndex = -1;
        target.focus();
      }
    });
  }

  // ── Low-vision toggle ────────────────────────────────────────
  private initVisionToggle(): void {
    if (localStorage.getItem("lowvision") === "true") {
      document.body.classList.add("lowvision");
      this.swapVideoSources(true);
      this.swapImageSources(true);
    }

    const buttons = qsa(
      "#changeVision, #changeVisionMobile, #changeVisionFooter, #changeVisionAccessibility, #changeVisionAccessibilityDesktop",
    );

    buttons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleVision();
      });

      btn.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter" || (e as KeyboardEvent).key === " ") {
          e.preventDefault();
          this.toggleVision();
        }
      });
    });

    this.updateVisionLabels();
  }

  private toggleVision(): void {
    document.body.classList.toggle("lowvision");
    const isLowVision = document.body.classList.contains("lowvision");
    localStorage.setItem("lowvision", isLowVision ? "true" : "false");

    this.updateVisionLabels();
    this.swapVideoSources(isLowVision);
    this.swapImageSources(isLowVision);

    if (!isLowVision) this.setFontSize(this.FONT_DEFAULT);
  }

  private updateVisionLabels(): void {
    const isLowVision = document.body.classList.contains("lowvision");
    const label = isLowVision ? this.LABEL_ON : this.LABEL_OFF;

    qsa(
      "#changeVision > span, #changeVisionMobile > span, #changeVisionFooter > span",
    ).forEach((span) => {
      span.textContent = label;
    });
  }

  // ── Video sources ────────────────────────────────────────────
  private swapVideoSources(isLowVision: boolean): void {
    document.querySelectorAll("video").forEach((v) => {
      const sourceEl = v.querySelector("source");
      const currentSrc =
        (sourceEl
          ? sourceEl.getAttribute("src")
          : v.getAttribute("src")) ?? "";
      if (!currentSrc) return;

      if (!v.dataset.colorSrc) v.dataset.colorSrc = currentSrc;

      if (!v.dataset.bwSrc) {
        const dotIdx = currentSrc.lastIndexOf(".");
        if (dotIdx > 0 && !currentSrc.includes("_bw.")) {
          v.dataset.bwSrc = currentSrc.substring(0, dotIdx) + "_bw.mp4";
        }
      }

      const newSrc = isLowVision ? v.dataset.bwSrc : v.dataset.colorSrc;
      if (newSrc && currentSrc !== newSrc) {
        if (sourceEl) {
          sourceEl.setAttribute("src", newSrc);
        } else {
          v.setAttribute("src", newSrc);
        }
        v.load();
        if (v.autoplay || !v.paused) {
          v.play().catch(() => {});
        }
      }
    });
  }

  // ── Image sources ────────────────────────────────────────────
  private swapImageSources(isLowVision: boolean): void {
    document.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src") ?? "";
      if (!src || src.startsWith("data:") || src.includes(".svg")) return;

      if (!img.dataset.colorSrc) {
        if (src.includes("_bw.")) return;
        img.dataset.colorSrc = src;
      }

      if (!img.dataset.bwSrc) {
        const dotIdx = src.lastIndexOf(".");
        if (dotIdx > 0) {
          img.dataset.bwSrc =
            src.substring(0, dotIdx) + "_bw" + src.substring(dotIdx);
        }
      }

      const newSrc = isLowVision
        ? (img.dataset.bwSrc ?? src)
        : (img.dataset.colorSrc ?? src);

      if (newSrc && src !== newSrc) {
        if (isLowVision) {
          img.onerror = function (this: HTMLImageElement) {
            this.onerror = null;
            this.src = this.dataset.colorSrc ?? src;
            this.style.filter = "grayscale(100%)";
          };
        } else {
          img.onerror = null;
          img.style.filter = "";
        }
        img.setAttribute("src", newSrc);

        if (img.srcset) {
          img.dataset.colorSrcset = img.dataset.colorSrcset ?? img.srcset;
        }
        if (isLowVision && img.dataset.colorSrcset) {
          img.srcset = "";
        } else if (!isLowVision && img.dataset.colorSrcset) {
          img.srcset = img.dataset.colorSrcset;
        }
      }
    });

    // Swap CSS background-images
    document
      .querySelectorAll<HTMLElement>('[style*="background-image"]')
      .forEach((el) => {
        const style = el.style.backgroundImage ?? "";
        const match = style.match(/url\(["']?([^"')]+)["']?\)/);
        if (!match?.[1]) return;
        const currentUrl = match[1];
        if (currentUrl.includes(".svg") || currentUrl.startsWith("data:"))
          return;

        if (!el.dataset.colorBg) {
          if (currentUrl.includes("_bw.")) return;
          el.dataset.colorBg = currentUrl;
        }

        if (isLowVision) {
          const dotIdx = currentUrl.lastIndexOf(".");
          if (dotIdx > 0 && !currentUrl.includes("_bw.")) {
            const bwUrl =
              currentUrl.substring(0, dotIdx) +
              "_bw" +
              currentUrl.substring(dotIdx);
            el.style.backgroundImage = `url(${bwUrl})`;
          }
        } else if (el.dataset.colorBg) {
          el.style.backgroundImage = `url(${el.dataset.colorBg})`;
        }
      });
  }

  // ── Font-size controls ───────────────────────────────────────
  private initFontSize(): void {
    const saved = parseInt(localStorage.getItem("fontSize") ?? "", 10);
    if (saved && saved >= this.FONT_MIN && saved <= this.FONT_MAX) {
      this.setFontSize(saved);
    }

    delegate("click", ".js_font_minus", (e) => {
      e.preventDefault();
      const current = this.getCurrentFontSize();
      if (current > this.FONT_MIN) {
        this.setFontSize(current - this.FONT_STEP);
      }
    });

    delegate("click", ".js_font_plus", (e) => {
      e.preventDefault();
      const current = this.getCurrentFontSize();
      if (current < this.FONT_MAX) {
        this.setFontSize(current + this.FONT_STEP);
      }
    });
  }

  private getCurrentFontSize(): number {
    const raw = document.documentElement.style.fontSize;
    return raw ? parseInt(raw, 10) : this.FONT_DEFAULT;
  }

  private setFontSize(percent: number): void {
    document.documentElement.style.fontSize = percent + "%";
    localStorage.setItem("fontSize", String(percent));

    qsa(".js_font_minus").forEach((el) => {
      el.classList.toggle("font-btn-dis", percent <= this.FONT_MIN);
    });
    qsa(".js_font_plus").forEach((el) => {
      el.classList.toggle("font-btn-dis", percent >= this.FONT_MAX);
    });
  }
}
